import { useCallback, useMemo, useState } from "react";
import type { FilterValue } from "@/components/filter-popover";
import type { SortDirection } from "@/types/table-column";

// Default sort settings
const DEFAULT_OPPORTUNITY_SORT_COLUMN = "name";
const DEFAULT_OPPORTUNITY_SORT_DIRECTION: SortDirection = "asc";

const DEFAULT_PERSON_SORT_COLUMN = "fullName";
const DEFAULT_PERSON_SORT_DIRECTION: SortDirection = "asc";

export type FilterType = "opportunities" | "people";

export function useCustomBuyingGroupFilters(type: FilterType) {
  // Initialize with default sort settings based on filter type
  const defaultSettings = useMemo(
    () => ({
      sortColumn:
        type === "opportunities"
          ? DEFAULT_OPPORTUNITY_SORT_COLUMN
          : DEFAULT_PERSON_SORT_COLUMN,
      sortDirection:
        type === "opportunities"
          ? DEFAULT_OPPORTUNITY_SORT_DIRECTION
          : DEFAULT_PERSON_SORT_DIRECTION,
    }),
    [type],
  );

  const [filters, setFilters] =
    useState<Record<string, unknown>>(defaultSettings);

  // Update a filter value
  const updateFilter = useCallback((key: string, value: FilterValue) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Clear a specific filter
  const clearFilter = useCallback((key: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
  }, []);

  // Clear all filters except sort settings
  const clearAllFilters = useCallback(() => {
    setFilters(defaultSettings);
  }, [defaultSettings]);

  // Get default sort settings
  const getDefaultSortSettings = useCallback(() => {
    return {
      sortColumn:
        type === "opportunities"
          ? DEFAULT_OPPORTUNITY_SORT_COLUMN
          : DEFAULT_PERSON_SORT_COLUMN,
      sortDirection:
        type === "opportunities"
          ? DEFAULT_OPPORTUNITY_SORT_DIRECTION
          : DEFAULT_PERSON_SORT_DIRECTION,
    };
  }, [type]);

  // Check if there are active filters (excluding sort settings)
  const hasFilters = useMemo(() => {
    return (
      Object.keys(filters).filter(
        (key) => key !== "sortColumn" && key !== "sortDirection",
      ).length > 0
    );
  }, [filters]);

  return {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
  };
}
